<template>
  <div
    class="zt-dropdown relative cursor-default items-center pl-4 pr-4 outline-none"
    :class="[
      bgColor,
      textColor,
      isFocused,
      isOutlined,
      isRounded,
      isDisabled,
      isSizeAdjustable,
    ]"
    :style="[isHover]"
    ref="ztDropdownContainer"
  >
    <zt-tooltip
      ref="ztTooltipElement"
      :text="selectedValue || label"
      :active="isTruncated"
      position="top"
      class="flex w-full items-center"
    >
      <input
        class="zt-dropdown-input flex w-full flex-auto cursor-pointer pl-0"
        ref="ztInputDropdown"
        :value="selectedValueTruncate"
        :class="[
          gtmClass,
          bgColor,
          isFocused,
          { 'zt-dropdown-input-disabled': $attrs.disabled },
        ]"
        :style="{
          width: widthCalculated,
        }"
        @click.stop="_onClick"
        @blur="_onBlur"
        v-bind="$attrs" v-on="$listeners"
        :id="componentId"
        :aria-owns="computedOwns"
        :placeholder="label"
        readonly
      />
      <div :class="[{ 'zt-input-icon-disabled': $attrs.disabled }]">
        <div
          v-if="selectedValue && clearable"
          @click="clearValue"
          class="icon-clearable ml-1 flex cursor-pointer items-center justify-center rounded-full text-zt-xs"
          :class="clearableColor"
        >
          <zt-icon name="times-solid" />
        </div>
        <zt-icon v-else name="caret-down" class="ml-1 mr-1 text-zt-lg" />
      </div>
    </zt-tooltip>
    <transition name="select-content">
      <div
        v-show="isActiveContent"
        ref="ztDropdownOptions"
        class="zt-dropdown-content absolute inline-block flex rounded bg-white"
        :style="cordsContent"
        :id="computedOwns"
      >
        <div
          class="zt-dropdown-list-box static block w-full overflow-y-auto overflow-x-hidden"
        >
          <slot :itemSelected="itemSelected" :containerOwns="computedOwns" />
        </div>
        <div v-if="activeSubMenu" id="subItemContainer" />
      </div>
    </transition>
  </div>
</template>

<script>
import utils from '../../../../utils/helpers'
import ztTooltip from '../ztTooltip/ztTooltip.vue'
import ztIcon from '../ztIcon/ztIcon.vue'

const getWidth = (font, elementValue) => {
  const text = document.createElement('span')
  text.style.fontFamily = font
  text.style.position = 'absolute'
  text.style.visibility = 'hidden'
  text.style.whiteSpace = 'nowrap'
  text.style.zIndex = '-1'
  text.innerHTML = elementValue
  document.body.appendChild(text)
  const rect = text.getBoundingClientRect()
  document.body.removeChild(text)
  return `${Math.ceil(rect.width + 1)}px`
}

export default {
  name: 'ztDropdown',
  inheritAttrs: false,
  components: {
    ztTooltip,
    ztIcon,
  },
  props: {
    value: {},
    label: {
      type: String,
      default: '',
    },
    itemText: {
      type: [String, Array],
      default: '',
    },
    itemValue: {
      type: [String, Array],
      default: '',
    },
    returnObject: {
      type: Boolean,
      default: false,
    },
    color: {
      type: String,
      default: '',
    },
    clearable: {
      type: Boolean,
      default: false,
    },
    outlined: {
      type: Boolean,
      default: false,
    },
    hover: {
      type: Boolean,
      default: false,
    },
    rounded: {
      type: String,
      default: '',
    },
    activeSubMenu: {
      type: Boolean,
      default: false,
    },
    defaultValue: {
      type: Object,
      default: () => ({}),
    },
    sizeAdjustable: {
      type: Boolean,
      default: false,
    },
    maxLength: {
      type: Number,
      default: 22,
    },
    activeTooltipItem: {
      type: Boolean,
      default: false,
    },
    gtmClass: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      componentId: '',
      isActiveContent: false,
      cordsContent: {},
      itemSelected: {},
      selectedValue: '',
      isHoverActive: false,
      fontFamily: '',
      widthCalculated: '',
      MAX_HEIGHT: '250',
    }
  },
  provide() {
    return {
      setItemSelected: this.setItemSelected,
      activeTooltipItem: this.activeTooltipItem,
      maxLength: this.maxLength,
    }
  },
  computed: {
    bgColor() {
      if (this.color && !this.outlined) {
        let colorLevel =
          this.color === 'primary' || this.color === 'secondary' ? '-200' : ''
        return `bg-zt-${this.color}` + colorLevel + ' text-zt-white'
      }
      if (this.selectedValue && !this.outlined) {
        if (this.color) {
          let colorLevel =
            this.color === 'primary' || this.color === 'secondary' ? '-200' : ''
          return `bg-zt-${this.color}` + colorLevel + ' text-zt-white'
        } else {
          return 'bg-zt-primary-100 text-zt-white'
        }
      }
      return !this.outlined ? 'bg-zt-dark-200' : ''
    },
    textColor() {
      if (this.color && this.outlined) {
        let colorLevel =
          this.color === 'primary' || this.color === 'secondary' ? '-200' : ''
        return `text-zt-${this.color}` + colorLevel
      }
      return 'text-zt-dark-500'
    },
    isOutlined() {
      if (this.outlined && this.color) {
        return (
          'zt-dropdown-outlined' +
          `${this.color ? ' zt-dropdown-outlined-' + this.color : ''}`
        )
      }
      return this.outlined ? 'zt-dropdown-outlined' : null
    },
    isHover() {
      if (this.hover && this.isHoverActive && this.outlined) {
        return {
          backgroundColor: 'currentColor',
        }
      }
      return ''
    },
    isRounded() {
      return this.rounded ? this.rounded : 'rounded-zt-lg'
    },
    isFocused() {
      if (this.isActiveContent) {
        return 'bg-zt-dark-300 placeholder-black'
      }
      return ''
    },
    isSizeAdjustable() {
      return this.sizeAdjustable ? 'inline-flex' : 'w-full max-w-full flex'
    },
    clearableColor() {
      if (this.selectedValue && !this.isActiveContent) {
        if (this.color) {
          let colorLevel =
            this.color === 'primary' || this.color === 'secondary' ? '-200' : ''
          return `bg-zt-white text-zt-${this.color}-${colorLevel}`
        } else {
          return 'bg-zt-white text-zt-primary-100'
        }
      }
      return ' bg-zt-dark-400 text-zt-white'
    },
    computedOwns() {
      return `list-${this._uid}`
    },
    isDisabled() {
      let disabledClass = ''
      if (this.$attrs.disabled && this.outlined) {
        disabledClass = 'zt-dropdown-outlined-disabled'
      }
      if (this.$attrs.disabled && !this.outlined) {
        disabledClass = 'zt-dropdown-disabled'
      }
      return disabledClass
    },
    selectedValueTruncate() {
      const text = this.selectedValue || this.label
      if (text.length > this.maxLength) {
        return text.slice(0, this.maxLength) + '...'
      }
      return text
    },
    isTruncated() {
      return this.selectedValueTruncate.length > this.maxLength
    },
  },
  watch: {
    isActiveContent(val) {
      if (val) {
        utils.insertContent(this.$refs.ztDropdownOptions)
      }
    },
  },
  mounted() {
    this.componentId = this._uid.toString()
    if (this.sizeAdjustable) {
      this.fontFamily = this.getFontFamily()
      this.getWidthCalculate()
    }
    if (this.defaultValue) this.setItemSelected(this.defaultValue)
  },
  beforeDestroy() {
    let [parent] = document.getElementsByTagName('body')
    if (
      parent &&
      this.$refs.ztDropdownOptions &&
      this.$refs.ztDropdownOptions.parentNode === parent
    ) {
      parent.removeChild(this.$refs.ztDropdownOptions)
    }
  },
  methods: {
    setItemSelected(item) {
      this.itemSelected = item
      this.selectedValue = this.getTextObject(this.itemSelected)
      this.$emit('input', this.itemSelected)
      this.$emit('change', this.itemSelected)
      this.getWidthCalculate()
      if (item.value) this.closeContent()
    },
    async _onClick(event) {
      this.$emit('focus', event)
      this.isActiveContent = true
      document.addEventListener('click', this.clickBlur)
      await this.$nextTick()
      this.cordsContent = this.positionContent()
    },
    _onBlur(event) {
      if (
        event.relatedTarget
          ? !event.relatedTarget.closest('.zt-dropdown-content')
          : false
      ) {
        this.closeContent()
      }
      this.$emit('blur', event)
    },
    clickBlur(event) {
      let optionsContent = event.target.closest('.zt-dropdown-content')
      if (!optionsContent) {
        this.closeContent()
        this.positionContent()
      }
    },
    closeContent() {
      this.isActiveContent = false
      document.removeEventListener('click', this.clickBlur)
    },
    positionContent() {
      let inputSelect = this.$refs.ztInputDropdown
      let selectOptions = this.$refs.ztDropdownOptions
      let container = this.$refs.ztDropdownContainer
      if (!inputSelect || !selectOptions || !container) return {}
      let topPos = 0
      let leftPos = 0
      let widthContent = 0
      let scrollContentTop = window.scrollY || document.documentElement.scrollTop
      if (
        inputSelect.getBoundingClientRect().top + selectOptions.scrollHeight + 20 >=
        window.innerHeight
      ) {
        const maxHeightMajor =
          selectOptions.clientHeight >= this.MAX_HEIGHT
            ? selectOptions.clientHeight / 2
            : ''
        topPos =
          inputSelect.getBoundingClientRect().top +
          inputSelect.clientHeight +
          scrollContentTop -
          selectOptions.scrollHeight +
          maxHeightMajor
      } else {
        topPos =
          inputSelect.getBoundingClientRect().top +
          scrollContentTop +
          inputSelect.clientHeight +
          2
      }
      leftPos = container.getBoundingClientRect().left
      widthContent = container.offsetWidth
      return {
        left: `${leftPos}px`,
        top: `${topPos}px`,
        'min-width': `${widthContent}px`,
        zIndex: 8,
      }
    },
    getTextObject(item) {
      return this.itemText === ''
        ? utils.getPropertyItem(item, 'text')
        : utils.getPropertyItem(item, this.itemText)
    },
    clearValue() {
      this.itemSelected = {}
      this.selectedValue = ''
      if (this.$refs.ztTooltipElement) {
        this.$refs.ztTooltipElement._mouseLeave()
      }
      this.$emit('input', this.itemSelected)
      this.$emit('change', this.itemSelected)
    },
    getFontFamily() {
      const element = this.$refs.ztInputDropdown || document.body
      return window.getComputedStyle(element, null).getPropertyValue('font-family')
    },
    getWidthCalculate() {
      if (this.sizeAdjustable) {
        this.widthCalculated = getWidth(
          this.fontFamily,
          this.selectedValueTruncate || this.label
        )
      }
    },
  },
}
</script>


